import React from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { PlannerDynamicSidebarContentType } from "@lifeat/shared/planner/PlannerTypes";
import { addDaysToDate } from "@lifeat/shared/util/DateUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import Tab from "@common/LifeAtTabs/Tab";
import Tabs from "@common/LifeAtTabs/Tabs";
import { useUserContext } from "@contexts/UserContext";
import CalendarWidgetToolbarIcon from "@icons/WidgetToolbar/Calendar-ToolbarWidget-Icon.svg";
import NotesWidgetToolbarIcon from "@icons/WidgetToolbar/Notes-ToolbarWidget-Icon.svg";
import ToDoWidgetToolbarIcon from "@icons/WidgetToolbar/ToDo-ToolbarWidget-Icon.svg";
import Styles from "@themes/Styles";
import UserMetricsButton from "@user/components/UserMetrics/UserMetricsButton";
import { updateLastViewedPlannerSidebarContent } from "@user/service/UserPreferencesService";
import FullScreenButton from "../../../../app/LifeAtApp/OverlayButtons/AllOverlayButtons/DisplayToggleButtons/FullScreenButton";
import ToggleSidebarButton from "../../../../app/LifeAtApp/TopNavigation/TopNavigationComponents/ToggleSidebarButton";
import UpgradeButton from "../../../../app/LifeAtApp/TopNavigation/TopNavigationComponents/UpgradeButton";
import { selectedDetail$ } from "../../TaskManagementGlobals";
import {
    SelectDynamicSidebarContentEvent,
    SelectPlannerBoardViewEvent,
    SelectPlannerCalendarViewEvent,
    SelectPlannerProjectsViewEvent,
} from "../AmplitudePlannerEvents";
import {
    dynamicSidebarContent$,
    filteringSidebarOpen$,
    firstVisibleDate$,
    mainContent$,
    viewingDate$,
} from "../PlannerGlobals";
import { PlannerBoardRef } from "./PlannerTaskBoard";
import ChevronLeftIcon from "/resources/icons/ChevronLeftArrow.svg";
import ChevronRightIcon from "/resources/icons/ChevronRightArrow.svg";

const TopNavigationContainer = styled.div`
    z-index: ${Styles.zIndexTopNavigation};
    height: ${Styles.topBarHeight};
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 8px;
    background-color: ${(props) => props.theme.backgroundPrimary};
    border-bottom: 1px solid ${(props) => props.theme.borderPrimary};
`;

const TopNavigationButtonContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const DatePickerContainer = styled(TopNavigationButtonContainer)`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StyledCalendarIcon = styled(CalendarWidgetToolbarIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledTaskListIcon = styled(ToDoWidgetToolbarIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledNotesIcon = styled(NotesWidgetToolbarIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const BOARD_VIEW_TAB_BTN_LABEL = "Board";
const CALENDAR_VIEW_TAB_BTN_LABEL = "Calendar";
const PROJECTS_VIEW_TAB_BTN_LABEL = "Projects";
const TODAY_BUTTON_TEXT = "Today";

const DISABLED_CALENDAR_TOOLTIP = "Switch to Board view to access the your daily calendar";

type PlannerTopNavigationProps = {
    boardRef: React.RefObject<PlannerBoardRef | null>;
};

const NUM_DAYS_IN_WEEK = 7;

function PlannerTopNavigation(props: PlannerTopNavigationProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { user } = useUserContext();

    const handleTodayBtnClick = () => {
        viewingDate$.value = new Date();
        props.boardRef.current?.scrollToToday();
    };

    const handleNavigateButtonClick = (numDays: number) => {
        const newDate = addDaysToDate(firstVisibleDate$.value, numDays);
        props.boardRef.current?.scrollToDate(newDate);
    };

    const handleBoardViewSelect = () => {
        mainContent$.value = "task-board";
        amplitudeService.logEvent(new SelectPlannerBoardViewEvent());
    };

    const handleCalendarViewSelect = () => {
        mainContent$.value = "calendar";
        amplitudeService.logEvent(new SelectPlannerCalendarViewEvent());
    };

    const handleProjectsViewSelect = () => {
        mainContent$.value = "projects";
        amplitudeService.logEvent(new SelectPlannerProjectsViewEvent());
    };

    const setDynamicSidebarContent = (content: PlannerDynamicSidebarContentType) => {
        dynamicSidebarContent$.value = content;
        amplitudeService.logEvent(new SelectDynamicSidebarContentEvent({ content }));
        if (user) {
            updateLastViewedPlannerSidebarContent(user.uid, content);
        }
    };

    return (
        <TopNavigationContainer>
            <TopNavigationButtonContainer>
                <ToggleSidebarButton
                    toggleSidebar={() => {
                        filteringSidebarOpen$.value = !filteringSidebarOpen$.value;
                    }}
                />
                <UpgradeButton />
                <Tabs>
                    <Tab
                        label={BOARD_VIEW_TAB_BTN_LABEL}
                        isActiveTab={mainContent$.value === "task-board"}
                        handleSelect={handleBoardViewSelect}
                        underlineColor={"red"}
                    />
                    <Tab
                        label={CALENDAR_VIEW_TAB_BTN_LABEL}
                        isActiveTab={mainContent$.value === "calendar"}
                        handleSelect={handleCalendarViewSelect}
                        underlineColor={"red"}
                    />
                    <Tab
                        label={PROJECTS_VIEW_TAB_BTN_LABEL}
                        isActiveTab={mainContent$.value === "projects"}
                        handleSelect={handleProjectsViewSelect}
                        underlineColor={"red"}
                    />
                </Tabs>
            </TopNavigationButtonContainer>

            {mainContent$.value === "task-board" && (
                <DatePickerContainer>
                    <LifeAtButtonV2 size={"small"} variant={"outline"} onClick={handleTodayBtnClick}>
                        {TODAY_BUTTON_TEXT}
                    </LifeAtButtonV2>
                    <LifeAtButtonV2
                        variant={"ghost"}
                        size={"small"}
                        onClick={() => handleNavigateButtonClick(-NUM_DAYS_IN_WEEK)}
                    >
                        <StyledChevronLeftIcon />
                    </LifeAtButtonV2>

                    <LifeAtButtonV2
                        variant={"ghost"}
                        size={"small"}
                        onClick={() => handleNavigateButtonClick(NUM_DAYS_IN_WEEK)}
                    >
                        <StyledChevronRightIcon />
                    </LifeAtButtonV2>
                </DatePickerContainer>
            )}

            <TopNavigationButtonContainer>
                <LifeAtButtonV2
                    variant={dynamicSidebarContent$.value === "task-list" ? "active" : "ghost"}
                    size={"small"}
                    onClick={() => {
                        if (dynamicSidebarContent$.value === "task-list") {
                            dynamicSidebarContent$.value = "none";
                            setDynamicSidebarContent("none");
                        } else {
                            setDynamicSidebarContent("task-list");
                        }
                    }}
                >
                    <StyledTaskListIcon />
                </LifeAtButtonV2>
                <div
                    data-tip={DISABLED_CALENDAR_TOOLTIP}
                    data-for={`disabled-calendar-sidebar`}
                    data-tooltip-place={"bottom"}
                >
                    <LifeAtButtonV2
                        isDisabled={mainContent$.value === "calendar"}
                        variant={dynamicSidebarContent$.value === "calendar-day" ? "active" : "ghost"}
                        size={"small"}
                        onClick={() => {
                            selectedDetail$.value = null;
                            if (dynamicSidebarContent$.value === "calendar-day") {
                                setDynamicSidebarContent("none");
                            } else {
                                setDynamicSidebarContent("calendar-day");
                            }
                        }}
                    >
                        <StyledCalendarIcon />
                    </LifeAtButtonV2>
                    {mainContent$.value === "calendar" && <ReactTooltip id={`disabled-calendar-sidebar`} />}
                </div>
                <LifeAtButtonV2
                    variant={dynamicSidebarContent$.value === "notes" ? "active" : "ghost"}
                    size={"small"}
                    onClick={() => {
                        selectedDetail$.value = null;
                        if (dynamicSidebarContent$.value === "notes") {
                            setDynamicSidebarContent("none");
                        } else {
                            setDynamicSidebarContent("notes");
                        }
                    }}
                >
                    <StyledNotesIcon />
                </LifeAtButtonV2>

                <UserMetricsButton />

                <FullScreenButton />
            </TopNavigationButtonContainer>
        </TopNavigationContainer>
    );
}

export default PlannerTopNavigation;
